import {
  DEFAULT_USER_PREFERENCES,
  DENSITY_PREFERENCES,
  THEME_PREFERENCES,
  type UserPreferences,
} from '@gestao/shared'
import type { ThemeName } from '@gestao/ui'

/**
 * Tema e densidade no `<html>` (`data-theme`, `data-density`). A última preferência aplicada
 * fica no localStorage só para o script de index.html pintar a primeira tela sem flash; a
 * fonte da verdade é a preferência do usuário na API.
 */
export const PREFERENCES_STORAGE_KEY = 'gestao.preferences'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function systemTheme(): ThemeName {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
    ? 'dark'
    : 'light'
}

export function resolveTheme(theme: UserPreferences['theme']): ThemeName {
  return theme === 'system' ? systemTheme() : theme
}

export function readStoredPreferences(): UserPreferences {
  try {
    const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY)
    const value = raw ? (JSON.parse(raw) as Partial<UserPreferences>) : {}
    return {
      theme:
        THEME_PREFERENCES.find((option) => option === value.theme) ??
        DEFAULT_USER_PREFERENCES.theme,
      density:
        DENSITY_PREFERENCES.find((option) => option === value.density) ??
        DEFAULT_USER_PREFERENCES.density,
    }
  } catch {
    return { ...DEFAULT_USER_PREFERENCES }
  }
}

function storePreferences(preferences: UserPreferences): void {
  try {
    window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences))
  } catch {
    // Armazenamento bloqueado: a próxima carga usa o tema do sistema até a sessão voltar.
  }
}

let unsubscribeSystem: (() => void) | null = null

/** Aplica tema e densidade, guarda para a próxima carga e segue o sistema quando for "system". */
export function applyPreferences(preferences: UserPreferences): void {
  const root = document.documentElement
  root.dataset.theme = resolveTheme(preferences.theme)
  root.dataset.density = preferences.density
  storePreferences(preferences)

  unsubscribeSystem?.()
  unsubscribeSystem = null
  if (preferences.theme === 'system' && typeof window.matchMedia === 'function') {
    const query = window.matchMedia(DARK_QUERY)
    const onChange = () => {
      root.dataset.theme = systemTheme()
    }
    query.addEventListener('change', onChange)
    unsubscribeSystem = () => query.removeEventListener('change', onChange)
  }
}
